import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledDateField, ControlledSelectField, ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { WorkOrderChangeStatusProps } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { GridItem } from 'uikit';

import { Grid } from '@consta/uikit/Grid';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useUerClosingWorkOrderChangeDefaults } from './hooks/useUerClosingWorkOrderChangeDefaults';
import { useUerCreateClosingWorkOrderChange } from './hooks/useUerCreateClosingWorkOrderChange';

import Style from './CreateClosingWorkOrderChange.module.css';

interface ICreateClosingWorkOrderChange {
    workOrderId: number;
    onClose: () => void;
    refreshProjectData: () => void;
}
const style = { width: 600 };

const statusesItems = [WorkOrderChangeStatusProps[WorkOrderChangeStatusEnum.Approved]];

const rules = { required: 'Обязательное поле' };

export const CreateClosingWorkOrderChange: React.FC<ICreateClosingWorkOrderChange> = (props) => {
    const navigate = useNavigate();
    const { id: projectId, methodology } = useProjectParams();
    const { workOrderId, onClose, refreshProjectData } = props;

    const { uerClosingWorkOrderChangeDefaultsDataItem, uerClosingWorkOrderChangeDefaultsStatus } =
        useUerClosingWorkOrderChangeDefaults({ workOrderId, onError: onClose });

    const { handleSubmit, control, reset, formState } = useForm({
        criteriaMode: 'all',
    });

    const { isDirty } = formState;

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const handleCloseModal = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [handleOpenConfirmationDialog, isDirty, onClose]);

    const onSuccess = useCallback(
        (workOrderChangeId: number) => {
            onClose();
            refreshProjectData();
            navigate(`/projects/${methodology}/${projectId}/orderChange/${workOrderChangeId}`);
        },
        [methodology, navigate, onClose, projectId, refreshProjectData],
    );

    const { uerCreateClosingWorkOrderChangeStatus, uerCreateClosingWorkOrderChangeMakeRequest } =
        useUerCreateClosingWorkOrderChange({
            workOrderId,
            onSuccess,
        });

    const onSubmit = useCallback(
        (data) => {
            uerCreateClosingWorkOrderChangeMakeRequest({
                description: data.description,
                internalNumber: data.internalNumber,
                signingDate: data.signingDate,
            });
        },
        [uerCreateClosingWorkOrderChangeMakeRequest],
    );

    useEffect(() => {
        if (uerClosingWorkOrderChangeDefaultsDataItem) {
            reset(uerClosingWorkOrderChangeDefaultsDataItem);
        }
    }, [reset, uerClosingWorkOrderChangeDefaultsDataItem]);

    return (
        <ModalDialog
            isOpen
            isOnClickOutsideDisable
            hasOverlay
            withTitleCloseButton
            title="данные закрывающего Изм. к нз"
            onClose={handleCloseModal}
            onConfirm={handleSubmit(onSubmit)}
            isCommit={uerCreateClosingWorkOrderChangeStatus === RequestStatus.FETCHING}
            btnConfirmDisabled={false}
            isLoading={uerClosingWorkOrderChangeDefaultsStatus === RequestStatus.FETCHING}
            style={style}
        >
            <div className={Style.createClosingWorkOrderChangeBody}>
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledSelectField
                            name="status"
                            label="Статус"
                            control={control}
                            items={statusesItems}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="contractSubjectText"
                            label="Предмет договора"
                            control={control}
                            type="text"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="workOrder"
                            label="Наряд-Заказ"
                            control={control}
                            type="text"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="internalNumber"
                            label="Номер Изм. к НЗ"
                            control={control}
                            type="text"
                            required
                            rules={rules}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField name="authoredBy" label="Автор" control={control} type="text" disabled />
                    </GridItem>
                    <GridItem>
                        <ControlledDateField name="updatedAt" label="Дата изменения" control={control} disabled />
                    </GridItem>
                    <GridItem>
                        <ControlledDateField
                            name="signingDate"
                            label="Дата подписания"
                            control={control}
                            required
                            rules={rules}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="description"
                            label="Комментарий"
                            control={control}
                            type="text"
                            placeholder="Введите"
                        />
                    </GridItem>
                </Grid>
            </div>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

CreateClosingWorkOrderChange.displayName = 'CreateClosingWorkOrderChange';
